import { useMemo } from "react";
import { css, cva } from "../../../styled-system/css";
import { hstack, vstack } from "../../../styled-system/patterns";
import { Color } from "../../api/schemas";
import svgDiagonalLineBlack from "../../assets/selectColor/diagonalLineBlack.svg";
import svgDiagonalLineWhite from "../../assets/selectColor/diagonalLineWhite.svg";
import { PageTitle } from "../../components/PageTitle";
import { SquareButton } from "../../components/buttons/squareButton/SquareButton";
import { Path } from "../../types/route";
import { getNumber } from "../../utils/number";
import { mwcProductURL } from "../../utils/url";

export interface SelectColorProps {
  colors: Color[];
  path: Path;
  setPath: (path: Path) => void;
  selectedColor: Color | undefined;
  tmpSelectedColorValue: string;
  onClickColor: (color: string) => void;
  isNextButtonDisabled: boolean;
}

const colorButtonStyle = cva({
  base: {
    cursor: "pointer",
    width: "104px",
    height: "54px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 8px",
    borderRadius: "4px",
    fontSize: "14px",
    lineHeight: "120%",
    letterSpacing: "0.04em",
    overflowWrap: "break-word",
    position: "relative",
  },
  variants: {
    isSelected: {
      true: {
        borderWidth: "4px",
        borderRadius: "4px",
        borderColor: "#8538F9",
        padding: "0 4px",
      },
      false: {},
    },
    forSale: {
      true: { cursor: "pointer" },
      false: {
        cursor: "not-allowed",
        opacity: 0.5,
      },
    },
  },
});

interface DiagonalLineProps {
  backgroundColorCode: string;
}

function DiagonalLine({ backgroundColorCode }: DiagonalLineProps) {
  const diagonalLineStyle = css({
    position: "absolute",
    top: "0",
    right: "0",
    width: "100%",
    height: "100%",
    zIndex: "1",
  });
  return (
    <>
      {backgroundColorCode === "ffffff" || backgroundColorCode === "FFFFFF" ? (
        <img className={diagonalLineStyle} src={svgDiagonalLineBlack} alt="" />
      ) : (
        <img className={diagonalLineStyle} src={svgDiagonalLineWhite} alt="" />
      )}
    </>
  );
}

export function SelectColor({
  colors,
  path,
  setPath,
  selectedColor,
  tmpSelectedColorValue,
  onClickColor,
  isNextButtonDisabled,
}: SelectColorProps) {
  const isNotSelectedColorForSale = useMemo(() => {
    if (!selectedColor?.value) return false;

    return (
      colors.filter(
        (_color) => _color.value === selectedColor.value && _color.for_sale,
      ).length === 0
    );
  }, [colors, selectedColor]);

  return (
    <div className={vstack({ width: "100%", gap: 0 })}>
      <div className={css({ width: "100%", maxWidth: "760px", px: 3 })}>
        <div className={css({ mb: 3 })}>
          <PageTitle>カラー選択</PageTitle>
        </div>
        <div className={css({ mb: 3, textAlign: "center" })}>
          {(selectedColor?.image_url ?? colors.length >= 1) && (
            <img
              className={css({
                display: "inline-block",
                width: "180px",
              })}
              src={selectedColor?.image_url ?? colors[0].image_url}
              alt="商品画像"
            />
          )}
        </div>

        {isNotSelectedColorForSale && (
          <span
            className={css({
              textStyle: "span.m.regular",
              color: "error.base",
              display: "block",
              width: "100%",
              textAlign: "center",
              mb: 3,
            })}
          >
            ※このカラーは現在お取り扱いがございません
          </span>
        )}

        <div
          className={css({
            position: "relative",
            width: "100%",
            mb: 4,
            textAlign: "center",
          })}
        >
          <span
            className={css({
              textStyle: "span.m.bold",
              position: "relative",
              display: "inline-block",
              bg: "black.100",
              zIndex: 2,
              pl: 1,
              pr: 1,
            })}
          >
            カラーを選んでください
          </span>
          <hr
            className={css({
              position: "absolute",
              top: "50%",
              width: "100%",
              zIndex: 1,
            })}
          />
        </div>

        <ul
          className={hstack({
            gap: 1,
            justifyContent: "flex-start",
            flexWrap: "wrap",
            width: "328px",
            margin: "0 auto",
          })}
        >
          {colors.map((color) => (
            <li key={color.value}>
              <button
                className={colorButtonStyle({
                  isSelected: selectedColor?.value === color.value,
                  forSale: color.for_sale,
                })}
                style={{
                  backgroundColor: `#${color.color_code}`,
                  color: color.color_code === "ffffff" ? "#000000" : "#ffffff",
                  textAlign: "left",
                }}
                onClick={() => {
                  if (!color.for_sale) return;
                  onClickColor(color.value);
                }}
              >
                {color.value}
                {!color.for_sale && (
                  <DiagonalLine backgroundColorCode={color.color_code} />
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div
        className={css({
          bg: "black.100",
          width: "100%",
          px: 3,
          position: "fixed",
          bottom: 0,
          left: 0,
          pt: 2,
          height: "134px",
          boxShadow: "0px 4px 15px 0px #00000040",
          zIndex: "stickeyFooter",
        })}
      >
        <div
          className={css({
            width: "100%",
            maxWidth: "760px",
            mx: "auto",
            textAlign: "center",
          })}
        >
          <SquareButton
            size="full"
            variant="contained"
            color="primary"
            onClick={() => {
              if (path === "select_color") {
                setPath("select_width_and_size");
              } else {
                setPath("confirm");
              }
            }}
            disabled={isNextButtonDisabled}
          >
            {path === "select_color" ? "次へ" : "変更する"}
          </SquareButton>
          <button
            className={css({ mt: 2, textStyle: "button.m" })}
            onClick={() => {
              if (path === "select_color") {
                location.href = mwcProductURL(getNumber());
              } else {
                onClickColor(tmpSelectedColorValue);
                setPath("confirm");
              }
            }}
          >
            戻る
          </button>
        </div>
      </div>
    </div>
  );
}
